import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { 
  Send, 
  User,
  MessageSquare,
  ArrowLeft,
  Check,
  CheckCheck,
  Paperclip,
  X,
  FileText,
  Download,
  Image as ImageIcon,
  Smile,
  Users,
  Plus,
  Settings,
  UserPlus,
  Trash2,
  ClipboardCheck,
  ChevronRight
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";

const EMOJI_OPTIONS = ['❤️', '👍', '🙏', '😊', '🎉', '👏', '💯', '🔥', '✝️', '🙌'];

export default function Messages({ user }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState({});
  const [messages, setMessages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [pendingCheckinRef, setPendingCheckinRef] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [groupUnreadCounts, setGroupUnreadCounts] = useState({});
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [activeEmojiMessage, setActiveEmojiMessage] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMessages, setGroupMessages] = useState([]);
  const [showMobileList, setShowMobileList] = useState(true); // Mobile view state
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const typingTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const selectedUserRef = useRef(null);
  const selectedGroupRef = useRef(null);
  const prefilledCheckinRef = useRef(null);

  // Keep refs in sync with state
  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);
  useEffect(() => {
    selectedGroupRef.current = selectedGroup;
  }, [selectedGroup]);

  useEffect(() => {
    fetchData();
    // Poll for new messages every 3 seconds
    const interval = setInterval(() => {
      fetchMessagesOnly();
      fetchUnreadCounts();
      fetchGroupUnreadCounts();
      fetchTypingStatus();
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Mount only – don't re-fetch full data on selection change; polling uses refs for current selection

  const prevMessageCountRef = useRef(0);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, []);

  useEffect(() => {
    // Only auto-scroll when new messages arrive (count increases),
    // not when switching conversations (count resets)
    const currentCount = selectedGroup ? groupMessages.length : messages.length;
    if (currentCount > prevMessageCountRef.current && prevMessageCountRef.current > 0) {
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 100);
      return () => clearTimeout(timer);
    }
    prevMessageCountRef.current = currentCount;
  }, [messages, groupMessages, selectedGroup, scrollToBottom]);

  // Store initial user order reference to prevent re-sorting on every render
  const userOrderRef = useRef(new Map());
  const initialLoadDoneRef = useRef(false);

  const fetchData = async () => {
    try {
      // Get users to message
      let availableUsers = [];
      if (user.role === 'student') {
        // Students can message teachers AND other students
        const [teachersRes, studentsRes] = await Promise.all([
          axios.get(`${API}/users/teachers`),
          axios.get(`${API}/users/students`)
        ]);
        // Combine teachers and students, excluding self
        const teachers = teachersRes.data.map(t => ({ ...t, isTeacher: true }));
        const students = studentsRes.data.filter(s => s.user_id !== user.user_id);
        availableUsers = [...teachers, ...students]
          .filter(u => u.user_id !== user.user_id)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      } else {
        // Teachers see students (already sorted alphabetically from backend)
        const usersRes = await axios.get(`${API}/users/students`);
        availableUsers = usersRes.data;
      }
      
      // Create a set of valid user IDs for filtering
      const validUserIds = new Set(availableUsers.map(u => u.user_id));

      // Get all messages to build conversation list
      const messagesRes = await axios.get(`${API}/messages`);
      const msgs = messagesRes.data;
      
      // Group messages by conversation partner (only include valid users)
      const convos = {};
      const latestMessageTime = {}; // Track latest message time per user
      const hasUnread = {}; // Track if user has unread messages
      
      msgs.forEach(msg => {
        const partnerId = msg.sender_id === user.user_id ? msg.receiver_id : msg.sender_id;
        // Only include conversations with existing users
        if (validUserIds.has(partnerId)) {
          if (!convos[partnerId]) {
            convos[partnerId] = [];
          }
          convos[partnerId].push(msg);
          
          // Track the latest message timestamp for sorting
          const msgTime = new Date(msg.created_at).getTime();
          if (!latestMessageTime[partnerId] || msgTime > latestMessageTime[partnerId]) {
            latestMessageTime[partnerId] = msgTime;
          }
          
          // Track unread messages (messages sent TO current user that are unread)
          if (msg.receiver_id === user.user_id && !msg.is_read) {
            hasUnread[partnerId] = true;
          }
        }
      });
      setConversations(convos);
      
      // Only sort on initial load to prevent flickering
      if (!initialLoadDoneRef.current) {
        // Sort users: unread first, then by most recent message, then alphabetically
        availableUsers.sort((a, b) => {
          const aUnread = hasUnread[a.user_id] || false;
          const bUnread = hasUnread[b.user_id] || false;
          
          // Users with unread messages come first
          if (aUnread && !bUnread) return -1;
          if (!aUnread && bUnread) return 1;
          
          const aHasConvo = latestMessageTime[a.user_id] || 0;
          const bHasConvo = latestMessageTime[b.user_id] || 0;
          
          // Users with conversations come before those without
          if (aHasConvo && !bHasConvo) return -1;
          if (!aHasConvo && bHasConvo) return 1;
          
          // Both have conversations - sort by most recent message
          if (aHasConvo && bHasConvo) {
            return bHasConvo - aHasConvo; // Descending (most recent first)
          }
          
          // Neither has conversations - sort alphabetically
          return (a.name || '').localeCompare(b.name || '');
        });
        
        // Store the order for future reference
        availableUsers.forEach((u, idx) => {
          userOrderRef.current.set(u.user_id, idx);
        });
        
        initialLoadDoneRef.current = true;
        setUsers([...availableUsers]);
      } else {
        // On subsequent loads, maintain the existing order
        // but update user data (like names, pictures)
        setUsers(prev => {
          // Create a map of current users for quick lookup
          const currentUserMap = new Map(prev.map(u => [u.user_id, u]));
          
          // Update existing users and add new ones
          const updatedUsers = availableUsers.map(newUser => {
            const existing = currentUserMap.get(newUser.user_id);
            return existing ? { ...existing, ...newUser } : newUser;
          });
          
          // Sort by stored order, new users go to end
          updatedUsers.sort((a, b) => {
            const aOrder = userOrderRef.current.get(a.user_id) ?? 999;
            const bOrder = userOrderRef.current.get(b.user_id) ?? 999;
            return aOrder - bOrder;
          });
          
          return updatedUsers;
        });
      }
      
      // Fetch groups
      const groupsRes = await axios.get(`${API}/groups`);
      setGroups(prev => {
        // Only update if groups changed
        if (JSON.stringify(prev.map(g => g.group_id)) !== JSON.stringify(groupsRes.data.map(g => g.group_id))) {
          return groupsRes.data;
        }
        return prev;
      });
      
      // For teachers, also fetch all students for group creation
      if (user.role === 'teacher') {
        const studentsRes = await axios.get(`${API}/users/students`);
        setAllStudents(studentsRes.data);
      }
      
      // Fetch unread counts
      await fetchUnreadCounts();
      await fetchGroupUnreadCounts();
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCounts = async () => {
    try {
      const response = await axios.get(`${API}/messages/unread-by-user`);
      // Only update if counts actually changed
      setUnreadCounts(prev => {
        const newCounts = response.data || {};
        const hasChanged = JSON.stringify(prev) !== JSON.stringify(newCounts);
        return hasChanged ? newCounts : prev;
      });
    } catch (error) {
      console.error("Error fetching unread counts:", error);
    }
  };

  const fetchGroupUnreadCounts = async () => {
    try {
      const response = await axios.get(`${API}/groups/unread`);
      setGroupUnreadCounts(prev => {
        const newCounts = response.data || {};
        const hasChanged = JSON.stringify(prev) !== JSON.stringify(newCounts);
        return hasChanged ? newCounts : prev;
      });
    } catch (error) {
      console.error("Error fetching group unread counts:", error);
    }
  };

  const fetchTypingStatus = async () => {
    try {
      const currentUser = selectedUserRef.current;
      const currentGroup = selectedGroupRef.current;
      if (currentUser) {
        const response = await axios.get(`${API}/typing/user/${currentUser.user_id}`);
        setTypingUsers(response.data.typing_users || []);
      } else if (currentGroup) {
        const response = await axios.get(`${API}/typing/group/${currentGroup.group_id}`);
        setTypingUsers(response.data.typing_users || []);
      }
    } catch (error) {
      console.debug("Typing status fetch failed:", error);
    }
  };

  const sendTypingStatus = async (typing) => {
    try {
      const targetId = selectedUser?.user_id || selectedGroup?.group_id;
      const targetType = selectedUser ? 'user' : 'group';
      if (targetId) {
        await axios.post(`${API}/typing?target_id=${targetId}&target_type=${targetType}&is_typing=${typing}`);
      }
    } catch (error) {
      console.debug("Typing status update failed:", error);
    }
  };

  const handleTyping = () => {
    if (!isTyping) {
      setIsTyping(true);
      sendTypingStatus(true);
    }
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set new timeout to stop typing after 10 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      sendTypingStatus(false);
    }, 10000);
  };

  const fetchMessages = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await axios.get(`${API}/messages/${selectedUser.user_id}`);
      setMessages(response.data);
      
      // Mark as read
      await axios.post(`${API}/messages/${selectedUser.user_id}/read`);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  // Lightweight polling function - only updates messages, not user list
  const fetchMessagesOnly = async () => {
    const currentUser = selectedUserRef.current;
    const currentGroup = selectedGroupRef.current;
    if (currentUser) {
      try {
        const response = await axios.get(`${API}/messages/${currentUser.user_id}`);
        // Only update if messages changed
        setMessages(prev => {
          const newMsgs = response.data;
          if (prev.length !== newMsgs.length || 
              (newMsgs.length > 0 && prev.length > 0 && 
               newMsgs[newMsgs.length - 1]?.message_id !== prev[prev.length - 1]?.message_id)) {
            return newMsgs;
          }
          return prev;
        });
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
    } else if (currentGroup) {
      try {
        const response = await axios.get(`${API}/groups/${currentGroup.group_id}/messages`);
        setGroupMessages(prev => {
          const newMsgs = response.data;
          if (prev.length !== newMsgs.length || 
              (newMsgs.length > 0 && prev.length > 0 && 
               newMsgs[newMsgs.length - 1]?.message_id !== prev[prev.length - 1]?.message_id)) {
            return newMsgs;
          }
          return prev;
        });
      } catch (error) {
        console.error("Error fetching group messages:", error);
      }
    }
  };

  const selectUser = useCallback(async (u) => {
    setSelectedUser(u);
    setSelectedGroup(null);
    setTypingUsers([]);
    setShowMobileList(false);
    prevMessageCountRef.current = 0; // Reset so initial scroll works
    try {
      const response = await axios.get(`${API}/messages/${u.user_id}`);
      setMessages(response.data);
      
      // Mark as read
      await axios.post(`${API}/messages/${u.user_id}/read`);
      
      // Update unread counts
      setUnreadCounts(prev => ({ ...prev, [u.user_id]: 0 }));
      
      // Scroll to bottom after messages load
      setTimeout(() => scrollToBottom(), 150);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  }, [scrollToBottom]);

  useEffect(() => {
    if (userId && users.length > 0) {
      const targetUser = users.find(u => u.user_id === userId);
      if (targetUser) {
        selectUser(targetUser);
      }
    }
  }, [userId, users, selectUser]);

  // Attach a check-in (as a link-preview card) when arriving from the Check-Ins review page (?checkin_id=...)
  useEffect(() => {
    const checkinId = searchParams.get("checkin_id");
    if (!checkinId || prefilledCheckinRef.current === checkinId || !selectedUser) return;
    prefilledCheckinRef.current = checkinId;
    axios.get(`${API}/checkins/${checkinId}`).then((res) => {
      const c = res.data;
      const fruits = c.fruits_of_spirit || {};
      const glorifying = c.glorifying_god || {};
      const fruitsAvg = Object.values(fruits).length ? Object.values(fruits).reduce((a, b) => a + b, 0) / Object.values(fruits).length : 0;
      const glorifyingAvg = Object.values(glorifying).length ? Object.values(glorifying).reduce((a, b) => a + b, 0) / Object.values(glorifying).length : 0;
      const overall = ((c.loved_well?.loved_god || 0) + (c.loved_well?.loved_people || 0) + fruitsAvg + glorifyingAvg) / 4;
      setPendingCheckinRef({
        checkin_id: c.checkin_id,
        week_start: c.week_start,
        overall_score: parseFloat(overall.toFixed(1)),
      });
      toast.success("Check-in attached — write your reply below");
    }).catch(() => {
      toast.error("Could not load that check-in to attach");
    });
  }, [searchParams, selectedUser]);

  const selectGroup = async (group) => {
    setSelectedGroup(group);
    setSelectedUser(null);
    setTypingUsers([]);
    setShowMobileList(false);
    prevMessageCountRef.current = 0; // Reset so initial scroll works
    try {
      const response = await axios.get(`${API}/groups/${group.group_id}/messages`);
      setGroupMessages(response.data);
      
      // Mark group messages as read
      await axios.post(`${API}/groups/${group.group_id}/read`);
      
      // Update group unread counts
      setGroupUnreadCounts(prev => ({ ...prev, [group.group_id]: 0 }));
      
      // Scroll to bottom after messages load
      setTimeout(() => scrollToBottom(), 150);
    } catch (error) {
      console.error("Error fetching group messages:", error);
    }
  };

  const handleBackToList = () => {
    setShowMobileList(true);
    setSelectedUser(null);
    setSelectedGroup(null);
  };

  const deleteConversation = async (userId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation? This will remove all messages.")) return;
    
    try {
      await axios.delete(`${API}/messages/thread/${userId}`);
      toast.success("Conversation deleted");
      
      // Clear selection if this user was selected
      if (selectedUser?.user_id === userId) {
        setSelectedUser(null);
        setMessages([]);
        setShowMobileList(true);
      }
      
      // Remove from conversations
      setConversations(prev => {
        const updated = { ...prev };
        delete updated[userId];
        return updated;
      });
      
      // Refresh data
      fetchData();
    } catch (error) {
      console.error("Error deleting conversation:", error);
      toast.error("Failed to delete conversation");
    }
  };

  const deleteGroup = async (groupId, e) => {
    e.stopPropagation();
    if (!confirm("Delete this group? This will remove all messages and the group.")) return;
    
    try {
      await axios.delete(`${API}/groups/${groupId}`);
      toast.success("Group deleted");
      
      // Clear selection if this group was selected
      if (selectedGroup?.group_id === groupId) {
        setSelectedGroup(null);
        setGroupMessages([]);
      }
      
      // Remove from groups list
      setGroups(prev => prev.filter(g => g.group_id !== groupId));
    } catch (error) {
      console.error("Error deleting group:", error);
      toast.error(error.response?.data?.detail || "Failed to delete group");
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() && pendingAttachments.length === 0 && !pendingCheckinRef) return;
    
    // Send to group or user
    if (selectedGroup) {
      await sendGroupMessage();
      return;
    }
    
    if (!selectedUser) return;

    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        receiver_id: selectedUser.user_id,
        content: newMessage,
        attachments: pendingAttachments,
        checkin_id: pendingCheckinRef?.checkin_id
      });
      setNewMessage('');
      setPendingAttachments([]);
      setPendingCheckinRef(null);
      fetchMessages();
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const sendGroupMessage = async () => {
    if (!selectedGroup) return;
    
    setSending(true);
    try {
      await axios.post(`${API}/groups/${selectedGroup.group_id}/messages`, {
        content: newMessage,
        attachments: pendingAttachments
      });
      setNewMessage('');
      setPendingAttachments([]);
      // Refresh group messages
      const response = await axios.get(`${API}/groups/${selectedGroup.group_id}/messages`);
      setGroupMessages(response.data);
    } catch (error) {
      console.error("Error sending group message:", error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const createGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    
    try {
      await axios.post(`${API}/groups`, {
        name: newGroupName,
        description: newGroupDesc,
        member_ids: selectedMembers
      });
      
      toast.success("Group created successfully!");
      setShowCreateGroup(false);
      setNewGroupName('');
      setNewGroupDesc('');
      setSelectedMembers([]);
      
      // Refresh groups
      const groupsRes = await axios.get(`${API}/groups`);
      setGroups(groupsRes.data);
    } catch (error) {
      console.error("Error creating group:", error);
      toast.error("Failed to create group");
    }
  };

  const toggleMember = (userId) => {
    setSelectedMembers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const isImage = file.type.startsWith('image/');
        setPendingAttachments(prev => [...prev, {
          url: `/api/files/${response.data.file_id}`,
          filename: file.name,
          type: isImage ? 'image' : 'document'
        }]);
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeAttachment = (index) => {
    setPendingAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleMessageLongPressStart = (messageId) => {
    const timer = setTimeout(() => {
      setActiveEmojiMessage(messageId);
    }, 500); // 500ms long press
    setLongPressTimer(timer);
  };

  const handleMessageLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const addReaction = async (messageId, emoji) => {
    try {
      await axios.post(`${API}/messages/${messageId}/reaction`, { emoji });
      setActiveEmojiMessage(null);
      fetchMessages();
      toast.success("Reaction added!");
    } catch (error) {
      console.error("Error adding reaction:", error);
      toast.error("Failed to add reaction");
    }
  };

  const getLastMessage = (partnerId) => {
    const convo = conversations[partnerId];
    if (!convo || convo.length === 0) return null;
    return convo[convo.length - 1];
  };

  // Get users that can be messaged (for new conversation dialog)
  const getFilteredUsersForNewConversation = () => {
    if (!searchQuery.trim()) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(u => 
      u.name?.toLowerCase().includes(query) || 
      u.email?.toLowerCase().includes(query)
    );
  };

  const startNewConversation = (targetUser) => {
    setShowNewConversation(false);
    setSearchQuery('');
    selectUser(targetUser);
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div data-testid="messages-page" className="h-[calc(100vh-180px)] min-h-[500px] flex flex-col">
        <div className="flex items-center mb-4 flex-shrink-0">
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
            Messages
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 flex-1 min-h-0">
          {/* Users/Conversations List - Hidden on mobile when chat is open */}
          <Card className={`border-slate-100 md:col-span-1 flex flex-col max-h-[75vh] md:max-h-[50vh] ${!showMobileList ? 'hidden md:flex' : 'flex'}`}>
            <CardHeader className="pb-3 flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">
                  Conversations
                </CardTitle>
                <div className="flex items-center gap-2">
                  {/* New Conversation Button - Available to all users */}
                  <Dialog open={showNewConversation} onOpenChange={setShowNewConversation}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8" data-testid="new-conversation-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        New
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Start New Conversation</DialogTitle>
                        <DialogDescription>
                          Select a {user.role === 'student' ? 'teacher or student' : 'student'} to message
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Input
                            placeholder="Search by name or email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full"
                            data-testid="user-search-input"
                          />
                        </div>
                        <div className="h-64 border rounded-md overflow-y-auto">
                          <div className="p-2 space-y-1">
                            {getFilteredUsersForNewConversation().length === 0 ? (
                              <p className="text-slate-500 text-sm text-center py-8">
                                No users found
                              </p>
                            ) : (
                              getFilteredUsersForNewConversation().map((u) => (
                                <div
                                  key={u.user_id}
                                  className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                                  onClick={() => startNewConversation(u)}
                                  data-testid={`user-option-${u.user_id}`}
                                >
                                  <Avatar className="w-10 h-10">
                                    <AvatarImage src={u.picture} alt={u.name} />
                                    <AvatarFallback className="bg-slate-200 text-slate-700">
                                      {getInitials(u.name)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-slate-900 truncate">
                                      {u.name}
                                      {u.isTeacher && <Badge variant="outline" className="ml-2 text-xs">Teacher</Badge>}
                                    </p>
                                    <p className="text-xs text-slate-500 truncate">{u.email}</p>
                                  </div>
                                  {conversations[u.user_id]?.length > 0 && (
                                    <Badge variant="secondary" className="text-xs">
                                      Has messages
                                    </Badge>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Group Creation - Teachers only */}
                  {user.role === 'teacher' && (
                  <Dialog open={showCreateGroup} onOpenChange={setShowCreateGroup}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8">
                        <Plus className="w-4 h-4 mr-1" />
                        Group
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Create Group Conversation</DialogTitle>
                        <DialogDescription>
                          Create a group to message multiple students at once
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label htmlFor="groupName">Group Name</Label>
                          <Input
                            id="groupName"
                            value={newGroupName}
                            onChange={(e) => setNewGroupName(e.target.value)}
                            placeholder="e.g., Bible Study Group"
                          />
                        </div>
                        <div>
                          <Label htmlFor="groupDesc">Description (optional)</Label>
                          <Textarea
                            id="groupDesc"
                            value={newGroupDesc}
                            onChange={(e) => setNewGroupDesc(e.target.value)}
                            placeholder="What is this group for?"
                            rows={2}
                          />
                        </div>
                        <div>
                          <Label>Select Members</Label>
                          <ScrollArea className="h-48 border rounded-md p-2 mt-2">
                            {allStudents.map((student) => (
                              <div
                                key={student.user_id}
                                className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer"
                                onClick={() => toggleMember(student.user_id)}
                              >
                                <Checkbox
                                  checked={selectedMembers.includes(student.user_id)}
                                  onCheckedChange={() => toggleMember(student.user_id)}
                                />
                                <Avatar className="w-8 h-8">
                                  <AvatarImage src={student.picture} />
                                  <AvatarFallback className="bg-slate-200 text-slate-700 text-xs">
                                    {getInitials(student.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-sm">{student.name}</span>
                              </div>
                            ))}
                          </ScrollArea>
                          <p className="text-xs text-slate-500 mt-1">
                            {selectedMembers.length} members selected
                          </p>
                        </div>
                        <Button onClick={createGroup} className="w-full">
                          <Users className="w-4 h-4 mr-2" />
                          Create Group
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                {/* Group Conversations */}
                {groups.length > 0 && (
                  <div className="p-2 border-b border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 px-2 mb-2">GROUPS</p>
                    {groups.map((group) => {
                      const isSelected = selectedGroup?.group_id === group.group_id;
                      return (
                        <div
                          key={group.group_id}
                          className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors group/item ${
                            isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'
                          }`}
                          onClick={() => selectGroup(group)}
                        >
                          <div className="relative">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <Users className="w-5 h-5 text-blue-600" />
                            </div>
                            {groupUnreadCounts[group.group_id] > 0 && (
                              <Badge className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 text-xs bg-red-500 hover:bg-red-500">
                                {groupUnreadCounts[group.group_id] > 99 ? '99+' : groupUnreadCounts[group.group_id]}
                              </Badge>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className={`font-medium truncate ${groupUnreadCounts[group.group_id] > 0 ? 'text-slate-900 font-semibold' : 'text-slate-900'}`}>{group.name}</p>
                              {groupUnreadCounts[group.group_id] > 0 && (
                                <span className="text-xs text-red-500 font-medium">
                                  {groupUnreadCounts[group.group_id]} new
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500">
                              {group.member_count} members
                            </p>
                          </div>
                          {user.role === 'teacher' && (
                            <button
                              onClick={(e) => deleteGroup(group.group_id, e)}
                              className="opacity-0 group-hover/item:opacity-100 p-2 rounded-full hover:bg-red-100 text-slate-400 hover:text-red-600 transition-all"
                              title="Delete group"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Direct Messages */}
                <div className="p-2">
                  <p className="text-xs font-semibold text-slate-500 px-2 mb-2">DIRECT MESSAGES</p>
                  {(() => {
                    // Filter to only show users with existing conversations
                    const usersWithConversations = users.filter(u => conversations[u.user_id]?.length > 0);
                    
                    if (usersWithConversations.length === 0) {
                      return (
                        <p className="text-slate-500 text-sm text-center py-8 px-4">
                          No conversations yet. Click &quot;New&quot; to start one.
                        </p>
                      );
                    }
                    
                    return (
                      <div className="space-y-1">
                        {usersWithConversations.map((u) => {
                          const lastMsg = getLastMessage(u.user_id);
                          const isSelected = selectedUser?.user_id === u.user_id;
                          const unreadCount = unreadCounts[u.user_id] || 0;
                          
                          return (
                            <div
                              key={u.user_id}
                              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors group/item ${
                                isSelected ? 'bg-slate-100' : 'hover:bg-slate-50'
                              }`}
                              onClick={() => selectUser(u)}
                            >
                              <div className="relative">
                                <Avatar className="w-10 h-10">
                                  <AvatarImage src={u.picture} alt={u.name} />
                                  <AvatarFallback className={`${isSelected ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-700'}`}>
                                    {getInitials(u.name)}
                                  </AvatarFallback>
                                </Avatar>
                                {unreadCount > 0 && (
                                  <Badge className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 text-xs bg-red-500 hover:bg-red-500">
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <div className={`font-medium truncate ${unreadCount > 0 ? 'text-slate-900 font-semibold' : 'text-slate-900'}`}>
                                    {u.name}
                                    {u.isTeacher && <Badge variant="outline" className="ml-2 text-xs">Teacher</Badge>}
                                  </div>
                                  {unreadCount > 0 && (
                                    <span className="text-xs text-red-500 font-medium ml-2">
                                      {unreadCount} new
                                    </span>
                                  )}
                                </div>
                                {lastMsg && (
                                  <div className="flex items-center justify-between gap-2">
                                    <p className={`text-xs truncate flex-1 ${unreadCount > 0 ? 'text-slate-700 font-medium' : 'text-slate-500'}`}>
                                      {lastMsg.content ? (lastMsg.content.length > 30 ? lastMsg.content.substring(0, 30) + '...' : lastMsg.content) : 'Attachment'}
                                    </p>
                                    <span className="text-[10px] text-slate-400 flex-shrink-0">
                                      {format(new Date(lastMsg.created_at), 'MMM d')}
                                    </span>
                                  </div>
                                )}
                              </div>
                              {user.role === 'teacher' && (
                                <button
                                  onClick={(e) => deleteConversation(u.user_id, e)}
                                  className="opacity-0 group-hover/item:opacity-100 p-2 rounded-full hover:bg-red-100 text-slate-400 hover:text-red-600 transition-all"
                                  title="Delete conversation"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Chat Area - Hidden on mobile when showing list */}
          <Card className={`border-slate-100 md:col-span-2 flex flex-col min-h-0 max-h-full ${showMobileList ? 'hidden md:flex' : 'flex'}`}>
            {selectedGroup ? (
              <>
                {/* Group Chat Header */}
                <CardHeader className="border-b border-slate-100 py-3 md:py-4 flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="md:hidden h-9 w-9"
                      onClick={handleBackToList}
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{selectedGroup.name}</p>
                      <p className="text-xs text-slate-500">{selectedGroup.member_count} members</p>
                    </div>
                  </div>
                </CardHeader>

                {/* Group Messages */}
                <div className="flex-1 min-h-0 overflow-hidden relative">
                  <div className="absolute inset-0 overflow-y-auto px-4 py-4 messages-scroll-container">
                    <div className="space-y-4">
                      {groupMessages.length === 0 ? (
                        <p className="text-slate-500 text-center py-8">
                          No messages yet. Start the conversation!
                        </p>
                      ) : (
                        groupMessages.map((msg) => {
                          const isMine = msg.sender_id === user.user_id;
                          return (
                            <div
                              key={msg.message_id}
                              className={`flex ${isMine ? 'justify-end' : 'justify-start'} gap-2`}
                            >
                              {!isMine && (
                                <Avatar className="w-8 h-8 flex-shrink-0">
                                  <AvatarImage src={msg.sender_picture} />
                                  <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
                                    {getInitials(msg.sender_name)}
                                  </AvatarFallback>
                                </Avatar>
                              )}
                              <div className={`max-w-[70%] ${isMine ? 'message-sent' : 'message-received'} px-4 py-3`}>
                                {!isMine && (
                                  <p className="text-xs font-medium text-blue-600 mb-1">{msg.sender_name}</p>
                                )}
                                <p className="text-sm break-words">{msg.content}</p>
                                <p className={`text-xs mt-1 ${isMine ? 'text-amber-100' : 'text-slate-400'}`}>
                                  {format(new Date(msg.created_at), 'h:mm a')}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      )}
                      
                      {/* Typing Indicator */}
                      {typingUsers.length > 0 && (
                        <div className="flex items-center gap-2 px-2">
                          <Avatar className="w-6 h-6">
                            <AvatarImage src={typingUsers[0]?.user_picture} />
                            <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
                              {getInitials(typingUsers[0]?.user_name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="bg-slate-100 rounded-full px-4 py-2 flex items-center gap-1">
                            <span className="text-xs text-slate-500">
                              {typingUsers.length === 1 
                                ? `${typingUsers[0]?.user_name} is typing`
                                : `${typingUsers.length} people are typing`
                              }
                            </span>
                            <span className="flex gap-1">
                              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div ref={messagesEndRef} />
                    </div>
                  </div>
                </div>

                {/* Group Message Input */}
                <div className="p-4 border-t border-slate-100 flex-shrink-0">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        handleTyping();
                      }}
                      placeholder="Type a message..."
                      className="flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={sending || !newMessage.trim()}
                      className="bg-blue-600 hover:bg-blue-700 rounded-full px-6"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : selectedUser ? (
              <>
                {/* Chat Header */}
                <CardHeader className="border-b border-slate-100 py-3 md:py-4 flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="md:hidden h-9 w-9"
                      onClick={handleBackToList}
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={selectedUser.picture} alt={selectedUser.name} />
                      <AvatarFallback className="bg-amber-100 text-amber-700">
                        {getInitials(selectedUser.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-slate-900">{selectedUser.name}</p>
                      <p className="text-xs text-slate-500">{selectedUser.email}</p>
                    </div>
                  </div>
                </CardHeader>

                {/* Messages */}
                <div className="flex-1 min-h-0 overflow-hidden relative">
                  <div className="absolute inset-0 overflow-y-auto px-4 py-4 messages-scroll-container">
                    <div className="space-y-4">
                      {messages.length === 0 ? (
                        <p className="text-slate-500 text-center py-8">
                          No messages yet. Start the conversation!
                        </p>
                      ) : (
                        messages.map((msg) => {
                          const isMine = msg.sender_id === user.user_id;
                          const hasAttachments = msg.attachments && msg.attachments.length > 0;
                          const hasReactions = msg.reactions && msg.reactions.length > 0;
                          
                          return (
                            <div
                              key={msg.message_id}
                              className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                            >
                              <div 
                                className={`max-w-[70%] ${isMine ? 'message-sent' : 'message-received'} px-4 py-3 relative group`}
                                onMouseDown={() => handleMessageLongPressStart(msg.message_id)}
                                onMouseUp={handleMessageLongPressEnd}
                                onMouseLeave={handleMessageLongPressEnd}
                                onTouchStart={() => handleMessageLongPressStart(msg.message_id)}
                                onTouchEnd={handleMessageLongPressEnd}
                              >
                                {/* Attachments */}
                                {hasAttachments && (
                                  <div className="mb-2 space-y-2">
                                    {msg.attachments.map((att, idx) => (
                                      <div key={idx}>
                                        {att.type === 'image' ? (
                                          <a href={att.url} target="_blank" rel="noopener noreferrer">
                                            <img 
                                              src={att.url} 
                                              alt={att.filename} 
                                              className="max-w-full rounded-lg max-h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                            />
                                          </a>
                                        ) : (
                                          <a 
                                            href={att.url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className={`flex items-center gap-2 p-2 rounded-lg ${isMine ? 'bg-white/20' : 'bg-slate-100'} hover:opacity-80 transition-opacity`}
                                          >
                                            <FileText className="w-4 h-4 flex-shrink-0" />
                                            <span className="text-sm truncate">{att.filename}</span>
                                            <Download className="w-4 h-4 flex-shrink-0" />
                                          </a>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {/* Check-In Link Preview */}
                                {msg.checkin_ref_id && (
                                  <button
                                    data-testid="checkin-preview-card"
                                    onClick={() => navigate(user.role === 'teacher' ? `/checkins?checkin_id=${msg.checkin_ref_id}` : `/checkin-history?checkin_id=${msg.checkin_ref_id}`)}
                                    className={`mb-2 w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                                      isMine ? 'bg-white/15 border-white/20 hover:bg-white/20' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                                    }`}
                                  >
                                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isMine ? 'bg-white/20' : 'bg-amber-100'}`}>
                                      <ClipboardCheck className={`w-4 h-4 ${isMine ? 'text-white' : 'text-amber-600'}`} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium">Weekly Check-In</p>
                                      <p className={`text-xs ${isMine ? 'text-slate-200' : 'text-slate-500'}`}>
                                        {msg.checkin_preview?.week_start ? `Week of ${format(new Date(msg.checkin_preview.week_start), 'MMM d, yyyy')}` : 'View details'}
                                        {msg.checkin_preview?.overall_score != null && ` · Overall ${msg.checkin_preview.overall_score}/10`}
                                      </p>
                                    </div>
                                    <ChevronRight className={`w-4 h-4 shrink-0 ${isMine ? 'text-slate-200' : 'text-slate-400'}`} />
                                  </button>
                                )}
                                {/* Message Text */}
                                {msg.content && <p className="text-sm">{msg.content}</p>}
                                <div className={`flex items-center gap-1 mt-1 ${isMine ? 'justify-end' : ''}`}>
                                  <span className={`text-xs ${isMine ? 'text-slate-300' : 'text-slate-500'}`}>
                                    Sent: {format(new Date(msg.created_at), 'MMM d, h:mm a')}
                                  </span>
                                  {isMine && (
                                    <span className="ml-1">
                                      {msg.is_read ? (
                                        <CheckCheck className="w-4 h-4 text-blue-400" />
                                      ) : (
                                        <Check className="w-4 h-4 text-slate-400" />
                                      )}
                                    </span>
                                  )}
                                </div>
                                {/* Read timestamp for sent messages */}
                                {isMine && msg.is_read && msg.read_at && (
                                  <div className={`mt-0.5 ${isMine ? 'text-right' : ''}`}>
                                    <span className="text-xs text-blue-300">
                                      Read: {format(new Date(msg.read_at), 'MMM d, h:mm a')}
                                    </span>
                                  </div>
                                )}
                                
                                {/* Reactions Display */}
                                {hasReactions && (
                                  <div className={`flex flex-wrap gap-1 mt-2 ${isMine ? 'justify-end' : 'justify-start'}`}>
                                    {msg.reactions.map((reaction, idx) => (
                                      <span 
                                        key={idx} 
                                        className="text-sm bg-white/80 rounded-full px-2 py-0.5 shadow-sm"
                                      >
                                        {reaction.emoji}
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {/* Emoji Picker Button (visible on hover) */}
                                <Popover open={activeEmojiMessage === msg.message_id} onOpenChange={(open) => !open && setActiveEmojiMessage(null)}>
                                  <PopoverTrigger asChild>
                                    <button
                                      className={`absolute ${isMine ? '-left-8' : '-right-8'} top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-slate-100 rounded-full`}
                                      onClick={() => setActiveEmojiMessage(msg.message_id)}
                                    >
                                      <Smile className="w-4 h-4 text-slate-400" />
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-2" side={isMine ? 'left' : 'right'}>
                                    <div className="flex gap-1">
                                      {EMOJI_OPTIONS.map((emoji) => (
                                        <button
                                          key={emoji}
                                          className="text-xl hover:scale-125 transition-transform p-1"
                                          onClick={() => addReaction(msg.message_id, emoji)}
                                        >
                                          {emoji}
                                        </button>
                                      ))}
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              </div>
                            </div>
                          );
                        })
                      )}
                      
                      {/* Typing Indicator for Direct Messages */}
                      {typingUsers.length > 0 && (
                        <div className="flex items-center gap-2 px-2">
                          <Avatar className="w-6 h-6">
                            <AvatarImage src={typingUsers[0]?.user_picture} />
                            <AvatarFallback className="bg-slate-100 text-slate-700 text-xs">
                              {getInitials(typingUsers[0]?.user_name || selectedUser?.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="bg-slate-100 rounded-full px-4 py-2 flex items-center gap-1">
                            <span className="text-xs text-slate-500">typing</span>
                            <span className="flex gap-1">
                              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div ref={messagesEndRef} />
                    </div>
                  </div>
                </div>

                {/* Message Input */}
                <div className="border-t border-slate-100 p-3 md:p-4 flex-shrink-0 safe-area-bottom">
                  {/* Pending Check-In Attachment Preview */}
                  {pendingCheckinRef && (
                    <div className="mb-3 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2 pl-3" data-testid="pending-checkin-chip">
                      <ClipboardCheck className="w-4 h-4 text-amber-600 shrink-0" />
                      <span className="text-sm text-amber-800 flex-1 truncate">
                        Check-In attached{pendingCheckinRef.week_start ? ` — Week of ${format(new Date(pendingCheckinRef.week_start), 'MMM d, yyyy')}` : ''}
                      </span>
                      <button
                        data-testid="remove-pending-checkin-btn"
                        onClick={() => setPendingCheckinRef(null)}
                        className="p-1 hover:bg-amber-100 rounded-full transition-colors"
                      >
                        <X className="w-3.5 h-3.5 text-amber-600" />
                      </button>
                    </div>
                  )}
                  {/* Pending Attachments Preview */}
                  {pendingAttachments.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {pendingAttachments.map((att, idx) => (
                        <div 
                          key={idx}
                          className="relative group bg-slate-100 rounded-lg p-2 flex items-center gap-2"
                        >
                          {att.type === 'image' ? (
                            <ImageIcon className="w-4 h-4 text-slate-500" />
                          ) : (
                            <FileText className="w-4 h-4 text-slate-500" />
                          )}
                          <span className="text-sm text-slate-700 max-w-[100px] truncate">{att.filename}</span>
                          <button
                            onClick={() => removeAttachment(idx)}
                            className="p-1 hover:bg-slate-200 rounded-full transition-colors"
                          >
                            <X className="w-3 h-3 text-slate-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex gap-2 items-end">
                    {/* Hidden File Input */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      multiple
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
                      className="hidden"
                    />
                    
                    {/* Attachment Button */}
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex-shrink-0 rounded-full h-10 w-10"
                      title="Attach file"
                    >
                      {uploading ? (
                        <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
                      ) : (
                        <Paperclip className="w-4 h-4" />
                      )}
                    </Button>
                    
                    <Input
                      data-testid="message-input"
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        handleTyping();
                      }}
                      placeholder="Type a message..."
                      className="flex-1 h-10 text-base"
                      onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    />
                    <Button
                      data-testid="send-message-btn"
                      onClick={sendMessage}
                      disabled={sending || (!newMessage.trim() && pendingAttachments.length === 0 && !pendingCheckinRef)}
                      className="bg-slate-800 hover:bg-slate-900 rounded-full h-10 w-10 md:w-auto md:px-6"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mt-2 text-center hidden md:block">
                    💡 Long press or hover on a message to add an emoji reaction
                  </p>
                </div>
              </>
            ) : (
              <CardContent className="flex-1 flex items-center justify-center hidden md:flex">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Select a conversation to start messaging</p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
}
